import { Controller } from "@hotwired/stimulus"

// Controller for managing mileage allowance distance ranges
// Handles adding/removing ranges and toggling between calculation modes
export default class extends Controller {
  static targets = [
    "modeRadio",
    "rangesContainer",
    "rangeRow",
    "fixedAmountInput",
    "fixedLabel",
    "rateInput",
    "rateLabel",
    "rateUnit",
  ]

  static values = {
    translations: Object,
  }

  connect() {
    // Initialize field visibility based on current mode
    this.updateMode()
  }

  // Get translation with fallback
  t(key) {
    return this.translationsValue[key] || key
  }

  // Update field visibility based on selected calculation mode
  updateMode() {
    const mode = this.getSelectedMode()
    const isFixedMode = mode === "fixed_per_range"

    // Toggle visibility of fixed amount fields
    this.fixedAmountInputTargets.forEach((input) => {
      input.classList.toggle("hidden", !isFixedMode)
    })
    this.fixedLabelTargets.forEach((label) => {
      label.classList.toggle("hidden", !isFixedMode)
    })

    // Toggle visibility of per-km rate fields
    this.rateInputTargets.forEach((input) => {
      input.classList.toggle("hidden", isFixedMode)
    })
    this.rateLabelTargets.forEach((label) => {
      label.classList.toggle("hidden", isFixedMode)
    })
    this.rateUnitTargets.forEach((unit) => {
      unit.classList.toggle("hidden", isFixedMode)
    })
  }

  // Get the currently selected calculation mode
  getSelectedMode() {
    const checkedRadio = this.modeRadioTargets.find((radio) => radio.checked)
    return checkedRadio ? checkedRadio.value : "fixed_per_range"
  }

  // Add a new range row
  addRange() {
    const container = this.rangesContainerTarget
    const mode = this.getSelectedMode()
    const isFixedMode = mode === "fixed_per_range"

    const newRow = document.createElement("div")
    newRow.className = "flex items-center space-x-2 range-row"
    newRow.dataset.mileageRangesTarget = "rangeRow"

    newRow.innerHTML = `
      <span class="text-sm text-gray-500 w-8">${this.t("range_from")}</span>
      <input type="number"
             data-range-field="from"
             value="0"
             step="0.1"
             min="0"
             placeholder="0"
             class="w-16 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <span class="text-sm text-gray-500">km</span>
      <span class="text-sm text-gray-500 w-8">${this.t("range_to")}</span>
      <input type="number"
             data-range-field="to"
             value=""
             step="0.1"
             min="0"
             placeholder="${this.t("unlimited")}"
             class="w-16 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <span class="text-sm text-gray-500">km</span>

      <span class="text-sm text-gray-500 ml-2 ${!isFixedMode ? "hidden" : ""}" data-mileage-ranges-target="fixedLabel">${this.t("fixed_amount")}</span>
      <input type="number"
             data-range-field="fixed_amount"
             data-mileage-ranges-target="fixedAmountInput"
             value="0"
             step="0.01"
             min="0"
             placeholder="0.00"
             class="w-20 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 ${!isFixedMode ? "hidden" : ""}">

      <span class="text-sm text-gray-500 ${isFixedMode ? "hidden" : ""}" data-mileage-ranges-target="rateLabel">${this.t("per_km_rate")}</span>
      <input type="number"
             data-range-field="per_km_rate"
             data-mileage-ranges-target="rateInput"
             value="0"
             step="0.01"
             min="0"
             placeholder="0.00"
             class="w-20 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 ${isFixedMode ? "hidden" : ""}">
      <span class="text-sm text-gray-500">&euro;<span data-mileage-ranges-target="rateUnit" class="${isFixedMode ? "hidden" : ""}">/km</span></span>

      <button type="button"
              class="p-1 text-gray-400 hover:text-red-500"
              data-action="click->mileage-ranges#removeRange"
              title="${this.t("remove_range")}">
        <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    `

    container.appendChild(newRow)
  }

  // Remove a range row
  removeRange(event) {
    const row = event.target.closest(".range-row")
    if (row && this.rangeRowTargets.length > 1) {
      row.remove()
      // Re-index remaining rows
      this.reindexRanges()
    }
  }

  // Re-index all range input names after a row is removed
  reindexRanges() {
    this.rangeRowTargets.forEach((row, index) => {
      const inputs = row.querySelectorAll('input[name*="mileage_ranges"]')
      inputs.forEach((input) => {
        const newName = input.name.replace(
          /\[mileage_ranges\]\[\d+\]/,
          `[mileage_ranges][${index}]`,
        )
        input.name = newName
      })
    })
  }
}
